import React, { useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, useTheme } from '@mui/material'
import { type GridRenderCellParams, type GridValidRowModel, type GridTreeNodeWithRender } from '@mui/x-data-grid'
import { Actions, TableModalAction, type Row } from '../../types'
import { red, shade } from '../../../../colors'
import GenericButton from '../../../GenericButton'
import { ButtonSize } from '../../../GenericButton/styles'
import CallMadeIcon from '@mui/icons-material/CallMade'
import { useNavigate } from 'react-router-dom'
interface ActionsWithDropdownProps {
  params: GridRenderCellParams<GridValidRowModel, any, any, GridTreeNodeWithRender>
  handleSelectedRow: () => void
  rowActions: Actions[]
  setActionModal: (arg: TableModalAction) => void
  onEditClick?: (row: Row) => void
  onPreviewClick?: (row: Row) => void
  onGoLiveClick?: (row: Row) => void
}

const ActionsWithDropdown: React.FC<ActionsWithDropdownProps> = ({
  params,
  handleSelectedRow,
  rowActions,
  setActionModal,
  onEditClick,
  onPreviewClick,
  onGoLiveClick
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  const handleAction = (action: TableModalAction): void => {
    handleSelectedRow()
    setActionModal(action)
    handleMenuClose()
  }

  const handleEdit = (): void => {
    onEditClick ? onEditClick(params?.row) : handleAction(TableModalAction.EDIT)
  }

  const handleDelete = (): void => {
    handleAction(TableModalAction.DELETE)
  }

  const handleArchive = (): void => {
    handleAction(TableModalAction.ARCHIVE)
  }
  const handlePreview = (): void => {
    if (params?.row) {
      onPreviewClick?.(params.row)
    } else {
      console.error('Error: params.row is undefined. Cannot proceed with Preview action.')
    }
  }

  const handleGoLIVE = (): void => {
    if (params?.row) {
      onGoLiveClick?.(params.row)
    } else {
      console.error('Error: params.row is undefined. Cannot proceed with Go Live action.')
    }
  }

  const showEditOption = rowActions?.includes(Actions.EDIT)
  const showDeleteOption = rowActions?.includes(Actions.DELETE)
  const showArchiveOption = rowActions?.includes(Actions.ARCHIVE)
  const showPreviewOption = rowActions?.includes(Actions.SURVEY_PREVIEW)
  const showGoLive = rowActions?.includes(Actions.GO_LIVE)

  const showActionDropdown = showDeleteOption || showArchiveOption || showPreviewOption || showGoLive

  return (
    <Box display='flex' justifyContent='center' alignItems='center' gap={1} >
      {showEditOption && (
        <GenericButton
          label='Edit'
          buttonSize={ButtonSize.Small}
          textTransform='capitalize'
          onClick={handleEdit}
          fontWeight='bold'

          style={{
            minWidth: theme.spacing(7),
            minHeight: theme.spacing(5),
            padding: '0',
            background: 'transparent'
          }}
        />
      )}
      <IconButton
        aria-label="more"
        aria-controls="action-dropdown"
        aria-haspopup="true"
        onClick={handleMenuClick}
      >
        <MoreVertIcon />
      </IconButton>
      {
        showActionDropdown &&
        <Menu
          id="action-dropdown"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          elevation={4}
          sx={{
            '& .MuiMenu-paper': {
              borderRadius: theme.spacing(2),
              width: theme.spacing(38),
              marginTop: theme.spacing(2),
              boxShadow: '0px 4px 6px -2px rgba(32, 41, 57, 0.03), 0px 12px 16px -4px rgba(32, 41, 57, 0.08)',
              border: `1px solid ${shade[650]}`
            }
          }}
        >

          {showPreviewOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handlePreview}>
              Preview Survey<CallMadeIcon sx={{ width: '22px', height: '20px' }} />
            </MenuItem>
          )}
          {showGoLive && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleGoLIVE}>
              Go Live Now
            </MenuItem>
          )}
          {showArchiveOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleArchive}>
              Archive
            </MenuItem>
          )}
          {showDeleteOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500', color: red[700] }} onClick={handleDelete}>
              Delete
            </MenuItem>

          )}
        </Menu>
      }
    </Box>
  )
}

export default ActionsWithDropdown
